import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Tooltip,
  Button,
  Select,
  Form,
  DatePicker,
  message,
  Input,
  Row,
  Col,
  Drawer,
  Typography,
  Tag,
} from "antd";
import {
  CloudDownloadOutlined,
  SearchOutlined,
  UserOutlined,
  CalendarOutlined,
  TagOutlined,
  BarChartOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  DollarCircleFilled,
  CheckCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  AlertCircle,
  ChevronRight,
  PhoneIncoming,
  PhoneOutgoing,
} from "lucide-react";
import dayjs from "dayjs";
import apiClient from 'services/apiClient';
import { useMyContext } from "Context/MyContextProvider";
import { motion } from "framer-motion";

import { UserSelectField } from "../CustomUtils.js/CommonFieldUtils";
import { getStatusIcon } from "components/shared-components/AvatarStatus";
import GReportsummary from "./GReportsummary";
import { ROW_GUTTER } from "constants/ThemeConstant";

const { RangePicker } = DatePicker;
const { Option } = Select;
const GlobalReports = () => {
  const {
    Permisson,
    navigate,
    HandleExport,
    userRole,
    isMobile,
  } = useMyContext();
  useEffect(() => {
    if (!Permisson?.includes("View Outgoing Reports")) {
      navigate("404");
    }
  }, [Permisson]);
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [userId, setUserId] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [summaryData, setSummaryData] = useState(null);
  const [summaryModalVisible, setSummaryModalVisible] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Check if any filter is applied
  const hasFilters = () => {
    return (
      userId ||
      startDate ||
      endDate ||
      category ||
      type ||
      status ||
      (searchValue && searchValue.trim())
    );
  };

  const fetchReports = async (searchTerm = "") => {
    setLoading(true);
    const params = {};

    if (startDate) {
      params.startDate = dayjs(startDate, "DD/MM/YY").format("DD/MM/YY");
    }
    if (endDate) {
      params.endDate = dayjs(endDate, "DD/MM/YY").format("DD/MM/YY");
    }
    if (searchTerm) {
      params.search = searchTerm.toLowerCase();
    }
    if (category) {
      params.category = category.toLowerCase();
    }
    if (type) {
      params.report_type = type;
    }
    if (status) {
      params.status = status;
    }
    if (userId) {
      params.user_id = userId;
    }
    try {
      const response = await apiClient.get(`/global-reports-fast`, {
        params,
      });
      if (!response.data.success) {
      }
      const data = response.data.data.reports;
      const summary = response.data.data.summary;
      setReports(data);
      setSummaryData(summary);
    } catch (error) {
      message.error("Error fetching reports");
    } finally {
      setLoading(false);
    }
  };



  // Track filter changes
  useEffect(() => {
    setFiltersChanged(true);
    setHasFetched(false); // Reset table view if filters change
  }, [userId, startDate, endDate, category, type, status, searchValue]);

  const handleGoClick = () => {
    fetchReports(searchValue);
    setFiltersChanged(false);
    setHasFetched(true);
  };

  const HandleUserChange = (value) => {
    setUserId(value);
  };

  const HandleDateRangeChange = (dates) => {
    if (dates && dates.length > 0) {
      const sortedDates = dates
        .slice()
        .sort((a, b) => new Date(a) - new Date(b));
      const formattedDates = sortedDates.map((date) =>
        dayjs(date).format("DD/MM/YY")
      );
      setStartDate(formattedDates[0]);
      setEndDate(formattedDates[formattedDates.length - 1]);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Force immediate fetch on Enter key
      if (hasFilters()) {
        fetchReports(searchValue);
      }
    }
  };

  const tableColumns = [
    {
      title: "Name",
      dataIndex: "company_name",
      render: (name, record) => (
        <div className="d-flex align-items-center">
          <UserOutlined className="mr-2" />
          <span>{name || record.company_name}</span>
        </div>
      ),
    },
    {
      title: "From",
      dataIndex: "display_phone_number",
      sorter: {
        compare: (a, b) =>
          a.display_phone_number.length - b.display_phone_number.length,
      },
    },
    {
      title: "To",
      dataIndex: "recipient_id",
      sorter: {
        compare: (a, b) => a.recipient_id.length - b.recipient_id.length,
      },
    },
    {
      title: "Message Category",
      dataIndex: "category",
      render: (status) => <div className="text-capitalize">{status}</div>,
      sorter: {
        compare: (a, b) => a.text_body.length - b.text_body.length,
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      align: "center",
      render: (type) => {
        if (type === "out_report") {
          return (
            <Tag color="blue" style={{ textTransform: "capitalize" }}>
              API
            </Tag>
          );
        } else if (type === "campaign_report") {
          return (
            <Tag color="orange" style={{ textTransform: "capitalize" }}>
              Campaign
            </Tag>
          );
        }
        return (
          <Tag color="default" style={{ textTransform: "capitalize" }}>
            {type}
          </Tag>
        );
      },
      sorter: {
        compare: (a, b) => a.type.length - b.type.length,
      },
    },
    {
      title: (
        <Tooltip title="Message delivery status - Sent, Delivered, Read, or Failed">
          <span>Status</span>
        </Tooltip>
      ),
      dataIndex: "status",
      render: (_, record) => {
        const dataErrorMessage = record?.error_message;

        if (record.status === "failed") {
          // Use error message from API response
          const displayMessage = dataErrorMessage || "Unknown error";

          const tooltipContent = <div>{displayMessage}</div>;

          // Create icon without tooltip to avoid double tooltip
          const iconElement = <AlertCircle size={16} className="text-danger" />;
          return <Tooltip title={tooltipContent}>{iconElement}</Tooltip>;
        }

        // For non-failed status, use the default getStatusIcon
        return getStatusIcon(record.status);
      },
      sorter: {
        compare: (a, b) => a.status.length - b.status.length,
      },
    },
    {
      title: "Date & Time",
      dataIndex: "updated_at",
      render: (updated_at) => {
        return updated_at ? updated_at : "N/A";
      },
      sorter: {
        compare: (a, b) => a.status.length - b.status.length,
      },
    },
  ];

  // Helper to summarize active filters
  const getActiveFiltersSummary = () => {
    const filters = [];
    if (startDate && endDate) filters.push(`Date: ${startDate} to ${endDate}`);
    if (userId) filters.push("User selected");
    if (category) filters.push(`Category: ${category}`);
    if (type) filters.push(`Type: ${type}`);
    if (status) filters.push(`Status: ${status}`);
    if (searchValue) filters.push(`Search: '${searchValue}'`);
    return filters.length ? filters.join(", ") : "No filters applied";
  };

  // Helper for expanded row details
  const expandData = (record) => {
    const labelColStyle = {
      minWidth: 140,
      color: "#888",
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexShrink: 0,
    };
    const valueColStyle = {
      fontWeight: 500,
      color: "#333",
      wordBreak: "break-all",
      flex: 1,
    };
    const rowStyle = {
      display: "flex",
      alignItems: "center",
      marginBottom: 8,
    };
    const fields = [
      { label: "User", icon: <UserOutlined />, value: record.user_name },
      // { label: 'Email', icon: <MailOutlined />, value: record.user_email },
      // { label: 'Company', icon: <ApartmentOutlined />, value: record.company_name },
      {
        label: "From",
        icon: <PhoneOutgoing size={14} />,
        value: record.display_phone_number,
      },
      {
        label: "To",
        icon: <PhoneIncoming size={14} />,
        value: record.recipient_id,
      },
      {
        label: "Category",
        icon: <TagOutlined />,
        value: record.category,
        isTag: true,
        tagColor: "geekblue",
      },
      {
        label: "Status",
        icon: <InfoCircleOutlined />,
        value: record.status,
        isTag: true,
        tagColor:
          record.status === "failed"
            ? "red"
            : record.status === "sent"
            ? "blue"
            : record.status === "delivered"
            ? "green"
            : "default",
      },
      {
        label: "Billable",
        icon: <DollarCircleFilled />,
        value: record.billable === "1" ? "Yes" : "No",
        isTag: true,
        tagColor: record.billable === "1" ? "success" : "default",
      },
      {
        label: "Created",
        icon: <CalendarOutlined />,
        value: record.created_at,
      },
      // { label: 'Delivered', icon: <CalendarOutlined />, value: record.delivered_time },
      // { label: 'Read', icon: <FieldTimeOutlined />, value: record.read_time },
      // { label: 'Conversation ID', icon: <IdcardOutlined />, value: record.conversation_id },
      {
        label: "Message ID",
        icon: <IdcardOutlined />,
        value: record.message_id,
      },
      // { label: 'Campaign ID', icon: <NumberOutlined />, value: record.campaign_id },
      {
        label: "Campaign Name",
        icon: <MessageOutlined />,
        value: record.campaign_name,
        isTag: true,
        tagColor: "purple",
      },
      {
        label: "Updated",
        icon: <CalendarOutlined />,
        value: record.updated_at,
      },
      // { label: 'Error Code', icon: <NumberOutlined />, value: record.error_code },
      {
        label: "Error Message",
        icon: <InfoCircleOutlined />,
        value: record.error_message,
        fullWidth: true,
      },
    ];

    return (
      <div style={{ padding: 16 }}>
        <Row gutter={[16, 0]}>
          {fields.map((field, index) => (
            <Col key={index} xs={24} sm={12} md={field.fullWidth ? 24 : 8}>
              <div style={rowStyle}>
                <span style={labelColStyle}>
                  {field.icon}
                  {field.label}:
                </span>
                {field.isTag ? (
                  <Tag color={field.tagColor} style={{ marginLeft: 4 }}>
                    {field.value || "-"}
                  </Tag>
                ) : (
                  <span style={valueColStyle}>{field.value || "-"}</span>
                )}
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };
  const cardItems = [
    {
      icon: (
        <CalendarOutlined
          style={{ fontSize: 24, color: "#1890ff", marginBottom: 8 }}
        />
      ),
      title: "Date Range",
      subtitle: "Choose period",
    },
    {
      icon: (
        <UserOutlined
          style={{ fontSize: 24, color: "#1890ff", marginBottom: 8 }}
        />
      ),
      title: "Select User",
      subtitle: "Filter by user",
    },
    {
      icon: (
        <TagOutlined
          style={{ fontSize: 24, color: "#1890ff", marginBottom: 8 }}
        />
      ),
      title: "Category",
      subtitle: "Filter by category",
    },
    //add item for type filter
    {
      icon: (
        <InfoCircleOutlined
          style={{ fontSize: 24, color: "#1890ff", marginBottom: 8 }}
        />
      ),
      title: "Type",
      subtitle: (
        <>
          Type <span className="fw-bold text-danger">API</span> covers all
          messages sent via API, chatbot, or other sources (not campaigns).
        </>
      ),
    },
    //add item for status filter
    {
      icon: (
        <CheckCircleOutlined
          style={{ fontSize: 24, color: "#1890ff", marginBottom: 8 }}
        />
      ),
      title: "Status",
      subtitle: "Filter by status",
    },
    {
      icon: (
        <SearchOutlined
          style={{ fontSize: 24, color: "#1890ff", marginBottom: 8 }}
        />
      ),
      title: "Search",
      subtitle: "Find content",
    },
  ];
  return (
    <>
      <Card bodyStyle={{ padding: "16px 24px" }}>
        <div className="container-fluid">
          <Form
            name="basicInformation"
            layout={`${isMobile ? "vertical" : "horizontal"} `}
          >
            {/* Header Row */}
            <Row
              gutter={[16, 16]}
              justify="space-between"
              align="middle"
              style={{ marginBottom: isMobile ? "10px" : "16px" }}
            >
              <Col xs={24} md={8}>
                <h2 style={{ margin: 0 }}>Global Reports</h2>
                <small
                  style={{ color: "#8c8c8c" }}
                  className="fw-bold text-danger"
                >
                  Note: By default, the date filter shows data for today (
                  {dayjs().format("DD/MM/YYYY")}).
                </small>
              </Col>
              <Col xs={24} md={16}>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <Tooltip title="Refresh">
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => fetchReports(searchValue)}
                      loading={loading}
                    />
                  </Tooltip>
                  <motion.div layout style={{ flex: 1, maxWidth: 180 }}>
                    <Button
                      icon={<CloudDownloadOutlined />}
                      onClick={() => HandleExport("export-global-reports", "global-report.xlsx")}
                      block
                    >
                      Export
                    </Button>
                  </motion.div>
                  {hasFetched && !loading && (
                    <motion.div
                      initial={{ x: 80, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: 80, opacity: 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      layout
                      style={{ flex: 1, maxWidth: 180 }}
                    >
                      <Button
                        icon={<BarChartOutlined />}
                        onClick={() => setSummaryModalVisible(true)}
                        type="default"
                        block
                      >
                        Summary
                      </Button>
                    </motion.div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Filters Row */}
            <Row
              gutter={[ROW_GUTTER]}
              align="end"
              style={{ marginBottom: "16px" }}
            >
              {/* Date Range Filter */}
              <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                <Form.Item className="mb-0" style={{ fontSize: "12px" }}>
                  <RangePicker
                    style={{ width: "100%" }}
                    allowClear
                    onChange={(dates) => HandleDateRangeChange(dates)}
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("day")
                    }
                  />
                </Form.Item>
              </Col>

              {/* User Filter */}
              {(userRole === "Admin" || userRole === "Reseller") && (
                <Col xs={12} sm={12} md={6} lg={4} xl={3}>
                  <Form.Item className="mb-0">
                    <UserSelectField
                      value={userId}
                      HandleUserChange={HandleUserChange}
                    />
                  </Form.Item>
                </Col>
              )}

              {/* Category Filter */}
              <Col xs={12} sm={12} md={6} lg={4} xl={3}>
                <Form.Item className="mb-0">
                  <Select
                    showSearch
                    placeholder="Category"
                    style={{ width: "100%" }}
                    onChange={(value) => setCategory(value)}
                    allowClear
                  >
                    <Option value="">All Categories</Option>
                    <Option value="service">Service</Option>
                    <Option value="utility">Utility</Option>
                    <Option value="marketing">Marketing</Option>
                    <Option value="authentication">Auth</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* Type Filter */}
              <Col xs={12} sm={12} md={6} lg={4} xl={3}>
                <Form.Item className="mb-0">
                  <Select
                    placeholder="Type"
                    style={{ width: "100%" }}
                    onChange={(value) => setType(value)}
                    allowClear
                  >
                    <Option value="">All Types</Option>
                    <Option value="out_report">API</Option>
                    <Option value="campaign_report">Campaign</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* Status Filter */}
              <Col xs={12} sm={12} md={6} lg={4} xl={3}>
                <Form.Item className="mb-0">
                  <Select
                    placeholder="Status"
                    style={{ width: "100%" }}
                    value={status}
                    onChange={(value) => setStatus(value)}
                    allowClear
                  >
                    <Option value="">All Status</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="sent">Sent</Option>
                    <Option value="delivered">Delivered</Option>
                    <Option value="read">Read</Option>
                    <Option value="failed">Failed</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* Search Filter */}
              <Col xs={24} sm={12} md={6} lg={4} xl={4}>
                <Form.Item className="mb-0">
                  <Input
                    placeholder="Search"
                    prefix={<SearchOutlined />}
                    value={searchValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                  />
                </Form.Item>
              </Col>

              {/* Go Button */}
              <Col xs={24} sm={12} md={4} lg={3} xl={2}>
                <Button
                  type="primary"
                  block
                  className="d-flex align-items-center justify-content-center"
                  disabled={!filtersChanged || loading}
                  onClick={handleGoClick}
                  // style={{ height: "40px" }}
                >
                  Go <ChevronRight />
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        {hasFetched ? (
          <div className="table-responsive">
            <Table
              loading={loading}
              columns={tableColumns}
              dataSource={reports}
              rowKey="id"
              locale={{
                emptyText: (
                  <div style={{ textAlign: "center", padding: 24 }}>
                    <div
                      style={{
                        fontSize: 32,
                        color: "#bfbfbf",
                        marginBottom: 8,
                      }}
                    >
                      📄
                    </div>
                    <div style={{ fontWeight: 500, marginBottom: 4 }}>
                      No reports found for your selected filters.
                    </div>
                    <div style={{ color: "#8c8c8c", fontSize: 13 }}>
                      {getActiveFiltersSummary()}
                    </div>
                  </div>
                ),
              }}
              expandable={{
                expandedRowRender: expandData,
                // rowExpandable: (record) => record.name !== 'Not Expandable',
              }}
            />
          </div>
        ) : (
          <div
            style={{
              padding: "20px",
              background: "#fafafa",
              margin: "20px",
              borderRadius: "8px",
            }}
          >
            <Row justify="center" align="middle">
              <Col span={24}>
                <Row justify="center" style={{ marginBottom: "24px" }}>
                  <Col>
                    <div
                      style={{
                        fontSize: "48px",
                        textAlign: "center",
                        color: "#1890ff",
                        marginBottom: "16px",
                      }}
                    >
                      🎯
                    </div>
                    <Typography.Title
                      level={4}
                      style={{ textAlign: "center", marginBottom: "8px" }}
                    >
                      Ready to Explore Your Reports?
                    </Typography.Title>
                    <Typography.Text
                      type="secondary"
                      style={{ display: "block", textAlign: "center" }}
                    >
                      Apply filters above to view detailed global reports and
                      analytics
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      className="text-center d-flex justify-content-center"
                      style={{ fontSize: "12px", textAlign: "center" }}
                    >
                      💡 Combine multiple filters for precise results
                    </Typography.Text>
                  </Col>
                </Row>

                <Row
                  gutter={[16, 16]}
                  justify="center"
                  style={{ marginBottom: "24px" }}
                >
                  {cardItems.map((item, index) => (
                    <Col key={index} xs={12} sm={6} md={6} lg={6} xl={4}>
                      <Card
                        size="small"
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ textAlign: "center", minHeight: "140px" }}
                      >
                        {item.icon}
                        <Typography.Text
                          strong
                          style={{ display: "block", fontSize: "12px" }}
                        >
                          {item.title}
                        </Typography.Text>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: "11px" }}
                        >
                          {item.subtitle}
                        </Typography.Text>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </div>
        )}
      </Card>
      <div className="">
        <Drawer
          title={
            <div style={{ textAlign: "center" }}>
              <BarChartOutlined
                style={{ color: "#1890ff", marginRight: "8px" }}
              />
              Global Reports Summary
            </div>
          }
          placement="right"
          width={isMobile ? "100%" : 600}
          onClose={() => setSummaryModalVisible(false)}
          open={summaryModalVisible}
          getContainer={false}
          style={{
            position: "absolute",
          }}
        >
          <GReportsummary summaryData={summaryData} />
        </Drawer>
      </div>
    </>
  );
};

export default GlobalReports;
